import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    ChartAccount,
    LocaleCode,
    OperationPreview,
    RecordableOperation,
    RecordedOperation,
} from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { PreviewOperationRequest } from '@/api/handlers/resources/operations/requests';
import {
    useChartOfAccounts,
    useOperationPreviewQuery,
    useRecordableOperationsQuery,
    useRecordOperationMutation,
} from '@/api/queries';
import {
    resolveLocale,
    todayIso,
    unwrapStatement,
} from '@/pages/accounting/statements/statementMeta';
import { newEntryId } from '@/pages/accounting/journalEntry/utils';
import { formatGel, parseGelToTetri } from '@/utils/money';
import { getAuthMethod, getUser } from '@/utils/authSession';
import { useDebouncedValue } from '@/utils/useDebouncedValue';
import {
    canEditOperations,
    canRecordOperations,
    deriveOperationsAccess,
    type OperationsAccess,
} from '../operationsAccess';
import {
    dateAvailability,
    dateStatus,
    emptyForm,
    entryShape,
    formForOperation,
    isComplete,
    jumpTarget,
    noOperationsCopy,
    periodNotice,
    postingPeriodLabel,
    soleOperation,
    PREVIEW_DEBOUNCE_MS,
    toRecordError,
    toSlotChoices,
    yesterdayIso,
    type DateStatus,
    type EntryShapeRow,
    type NoOperationsCopy,
    type PeriodNotice,
    type RecordError,
    type RecordForm,
} from './recordMeta';

export type UseRecordPageResult = {
    access: OperationsAccess;
    canRecord: boolean;
    locale: LocaleCode;
    /** The picker and the chart are still loading. */
    isLoading: boolean;
    /** A failed *read* — thrown by the page's content child, never rendered inline. */
    error: ErrorResponse | null;
    onRetry: () => void;
    operations: RecordableOperation[];
    /**
     * The date band's status line, or `null` while the picker's read is in
     * flight — "not known yet", never "none".
     */
    dateStatus: DateStatus | null;
    /** The empty state's copy, set only when `availability` is not `available`. */
    noOperationsCopy: NoOperationsCopy | null;
    /** Whether the picker's dead-end footer may link to the Templates tab. */
    canEditTemplates: boolean;
    /** The date the "jump to" button moves to, or `null` when there is nowhere. */
    jumpTo: string | null;
    onDateChange: (date: string) => void;
    onJumpToEarliest: () => void;
    onToday: () => void;
    onYesterday: () => void;
    /** The period gate's warning for this date, or `null` when nothing is in the way. */
    periodNotice: PeriodNotice | null;
    /** The refusal anchored to the date field, or `null`. */
    dateError: string | null;
    selected: RecordableOperation | null;
    onSelect: (key: string) => void;
    /**
     * Return to the picker. `null` when there is nothing to change to — this
     * session may record exactly one operation.
     */
    onChangeOperation: (() => void) | null;
    /**
     * The recipe's lines, drawn from the moment of the pick. Empty until one is
     * picked, and **not authoritative** — see `entryShape`.
     */
    shape: EntryShapeRow[];
    /** True once the server's own preview totals agree. Never inferred. */
    isBalanced: boolean;
    /** `August 2026` for the entry's date, or `null` before a pick. */
    periodLabel: string | null;
    /**
     * The amount the Post button names, formatted, or `null` while there is
     * nothing to name. Taken from the *server's* total once one exists — the
     * button should say what will post, not what was typed.
     */
    postAmountLabel: string | null;
    form: RecordForm;
    onFieldChange: (patch: Partial<RecordForm>) => void;
    onSlotChange: (lineId: string, accountId: string) => void;
    accounts: ChartAccount[];
    /** The server's posting preview for what is on screen, or `null`. */
    preview: OperationPreview | null;
    isPreviewing: boolean;
    /** True while the form has changed but the debounced preview has not caught up. */
    isPreviewStale: boolean;
    /** The amount could not be parsed. `null` while the field is empty. */
    amountError: 'invalid' | null;
    /** The last refusal, anchored to the part of the form that caused it. */
    formError: RecordError | null;
    canPost: boolean;
    isPosting: boolean;
    onPost: () => void;
    /** The last successful posting, until it is dismissed. */
    recorded: RecordedOperation | null;
    onDismissRecorded: () => void;
};

/**
 * Drives the Record tab.
 *
 * Two reads and one write. The reads are the picker
 * (`GET /ledger/operations/recordable?date=`, already filtered to what this
 * session's role may record) and the chart, which the slot dropdowns are built
 * from. The write posts.
 *
 * **The impact panel is fed by the server, not by arithmetic here.** The editor
 * has a client-side preview and this deliberately does not reuse it: that one
 * answers "what would this *recipe* book for a sample amount", which is a
 * question about a template. This one has to answer "what will *this request*
 * book — these accounts, this date, the VAT rate in force then" and then a Post
 * button sits directly under the answer. Only the server can be authoritative
 * about that, and `preview` and `record` share one pipeline server-side
 * precisely so the two cannot disagree.
 *
 * **Posting is immediate and there is no draft.** So the gate on the Post button
 * is a successful preview, not the form's own opinion: if the server would
 * refuse the request, the user finds out before pressing the button rather than
 * after.
 */
export const useRecordPage = (): UseRecordPageResult => {
    const { i18n, t } = useTranslation('pages');
    const locale = resolveLocale(i18n.language);

    const access = React.useMemo(
        () => deriveOperationsAccess(getUser(), getAuthMethod()),
        [],
    );
    const canRecord = canRecordOperations(access);

    const [form, setForm] = React.useState<RecordForm>(() =>
        emptyForm(todayIso()),
    );
    const [selectedKey, setSelectedKey] = React.useState<string | null>(null);
    const [recorded, setRecorded] = React.useState<RecordedOperation | null>(
        null,
    );
    const [postError, setPostError] = React.useState<RecordError | null>(null);

    /**
     * The idempotency key for the current submission attempt-series.
     *
     * One id survives every retry of the same submission — which is what makes a
     * retry after a dropped response return the original entry instead of
     * booking the expense twice — and is regenerated only once a post succeeds,
     * so recording the same expense again tomorrow is a genuinely new entry.
     */
    const attemptIdRef = React.useRef(newEntryId());

    // The picker is a function of the date: which version is in force is
    // resolved from the entry's own date, so moving the date is a different
    // question rather than a stale answer to the same one.
    const recordableQuery = useRecordableOperationsQuery(form.date, canRecord);
    const { data: recordable, error: recordableError } = unwrapStatement(
        recordableQuery.data,
        recordableQuery.error,
    );

    const chart = useChartOfAccounts({ locale, enabled: canRecord });

    const operations = React.useMemo(
        () => recordable?.items ?? [],
        [recordable],
    );

    /**
     * The whole of what the date is saying, in one derivation.
     *
     * `null` until the read lands. An empty list under a resolved response and
     * an empty list under a pending one are different claims, and the date band
     * must not render "0 available" for the second.
     */
    const availability = React.useMemo(
        () => (recordable ? dateAvailability(recordable) : null),
        [recordable],
    );

    const jumpTo = availability ? jumpTarget(availability) : null;

    /**
     * The one operation this session may record, or `null`.
     *
     * Resolved rather than offered: see `soleOperation`. It is derived, not set
     * by an effect, so the choice cannot briefly render as "nothing picked" and
     * cannot survive a date change that adds a second operation.
     */
    const sole = React.useMemo(() => soleOperation(operations), [operations]);

    const selected = React.useMemo(
        () =>
            operations.find((item) => item.key === selectedKey) ?? sole ?? null,
        [operations, selectedKey, sole],
    );

    const amountTetri = React.useMemo(() => {
        const parsed = parseGelToTetri(form.amountGel);
        return parsed.ok ? parsed.tetri : null;
    }, [form.amountGel]);

    const amountError: 'invalid' | null =
        form.amountGel.trim().length > 0 && amountTetri === null
            ? 'invalid'
            : null;

    /**
     * The request the preview would send, or `null` when there is nothing
     * complete enough to ask about.
     *
     * A half-filled form previews as **nothing**, never as a partial entry: the
     * panel is a promise about what will post, and an entry drawn from three of
     * four answered slots is a promise about something nobody is going to submit.
     */
    const previewBody: PreviewOperationRequest | null = React.useMemo(() => {
        if (
            !selected ||
            selected.unrecordableReason !== 'NONE' ||
            amountTetri === null ||
            amountTetri <= 0 ||
            !isComplete(form, selected)
        ) {
            return null;
        }
        return {
            date: form.date,
            amountTetri,
            slots: toSlotChoices(form, selected),
        };
    }, [amountTetri, form, selected]);

    // Debounced so typing an amount costs one request, not one per keystroke.
    // The debounce is here rather than in the query hook so the key and the
    // request can never describe different forms.
    const debouncedBody = useDebouncedValue(previewBody, PREVIEW_DEBOUNCE_MS);
    const isPreviewStale =
        previewBody !== null && debouncedBody !== previewBody;

    const previewQuery = useOperationPreviewQuery(
        selected?.key ?? '',
        debouncedBody,
        canRecord,
    );
    const previewResult = previewQuery.data;
    const preview =
        previewResult?.success && !isPreviewStale
            ? previewResult.response
            : null;

    const record = useRecordOperationMutation();

    /**
     * The refusal on screen.
     *
     * A post's refusal wins over a preview's while it stands, because it is the
     * newer answer and it is the one the user just asked for. A preview refusal
     * is otherwise the everyday case — it is how a closed period or an
     * out-of-filter account gets named before the button is pressed.
     */
    const formError = React.useMemo(() => {
        if (postError) {
            return postError;
        }
        if (previewResult && !previewResult.success && !isPreviewStale) {
            return toRecordError(previewResult.error);
        }
        return null;
    }, [isPreviewStale, postError, previewResult]);

    const onSelect = React.useCallback(
        (key: string) => {
            setSelectedKey(key);
            setPostError(null);
            setRecorded(null);
            const operation = operations.find((item) => item.key === key);
            if (operation) {
                setForm((current) => formForOperation(current, operation));
            }
        },
        [operations],
    );

    /**
     * Back to the picker.
     *
     * The slot answers go with it: they are answers to *that* recipe's
     * questions, and a line id from another template means nothing to the next
     * one. The amount, the reference and the note stay — they are about the
     * event, not about how it is booked. The picker's search term clears itself,
     * because the picker unmounts.
     */
    const onChangeOperation = React.useCallback(() => {
        setSelectedKey(null);
        setPostError(null);
        setRecorded(null);
        setForm((current) => ({ ...current, slots: {} }));
    }, []);

    const onFieldChange = React.useCallback((patch: Partial<RecordForm>) => {
        setForm((current) => ({ ...current, ...patch }));
        // Clearing on the next edit rather than on the next submit is what stops
        // a stale "period 6 is CLOSED" sitting under a date the user has since
        // moved.
        setPostError(null);
    }, []);

    /**
     * Moving the date is the same edit as typing in the field, so it goes
     * through `onFieldChange` — which is also what clears a stale refusal that
     * named the old date.
     */
    const onDateChange = React.useCallback(
        (date: string) => {
            if (date) {
                onFieldChange({ date });
            }
        },
        [onFieldChange],
    );

    const onToday = React.useCallback(
        () => onDateChange(todayIso()),
        [onDateChange],
    );

    const onYesterday = React.useCallback(
        () => onDateChange(yesterdayIso()),
        [onDateChange],
    );

    const onJumpToEarliest = React.useCallback(() => {
        if (jumpTo) {
            onDateChange(jumpTo);
        }
    }, [jumpTo, onDateChange]);

    const onSlotChange = React.useCallback(
        (lineId: string, accountId: string) => {
            setForm((current) => ({
                ...current,
                slots: { ...current.slots, [lineId]: accountId },
            }));
            setPostError(null);
        },
        [],
    );

    const canPost =
        canRecord && preview !== null && !record.isPending && !isPreviewStale;

    const onPost = React.useCallback(() => {
        if (!selected || !previewBody) {
            return;
        }
        setPostError(null);
        record.mutate(
            {
                key: selected.key,
                body: {
                    ...previewBody,
                    id: attemptIdRef.current,
                    reference: form.reference,
                    note: form.note,
                },
            },
            {
                onSuccess: (result) => {
                    if (!result.success) {
                        setPostError(toRecordError(result.error));
                        return;
                    }
                    setRecorded(result.response);
                    // A new attempt-series: this expense is booked, and the next
                    // one is a different entry even if every field matches.
                    attemptIdRef.current = newEntryId();
                    // The date, the operation and the reference stay. Someone
                    // recording a run of receipts is doing the same thing again
                    // with a different figure, and clearing the whole form would
                    // make them re-answer questions they just answered.
                    setForm((current) => ({
                        ...current,
                        amountGel: '',
                        note: '',
                    }));
                },
            },
        );
    }, [form.note, form.reference, previewBody, record, selected]);

    const onDismissRecorded = React.useCallback(() => setRecorded(null), []);

    const onRetry = React.useCallback(() => {
        recordableQuery.refetch();
    }, [recordableQuery]);

    return {
        access,
        canRecord,
        locale,
        // The chart is part of the form, not decoration: without it every slot
        // dropdown is empty. So the tab is loading until both have landed.
        isLoading:
            canRecord &&
            (recordableQuery.isLoading ||
                recordable === null ||
                chart.isLoading),
        // Only the picker's failure is thrown. A failed *chart* read leaves the
        // dropdowns empty, which the form already renders as "no account to
        // choose", and throwing it would take down a tab whose picker loaded.
        error: recordableError,
        onRetry,
        operations,
        dateStatus: availability ? dateStatus(availability) : null,
        noOperationsCopy:
            availability && availability.kind !== 'available'
                ? noOperationsCopy(availability)
                : null,
        // The dead-end footer links to Templates only for someone who may
        // actually edit one. For everyone else the sentence beside it — recipes
        // are created by an accountant — is the whole answer, and a link into a
        // read-only catalog would be a longer way of saying no.
        canEditTemplates: canEditOperations(access),
        jumpTo,
        onDateChange,
        onJumpToEarliest,
        onToday,
        onYesterday,
        periodNotice: recordable ? periodNotice(recordable.periodStatus) : null,
        // The date's own refusal — a closed period, a version that does not
        // resolve — rendered on the field that caused it, which has moved above
        // the picker. `beforeEarliest` is not one of these: it is the client's
        // own reading of the counts, not something the server refused.
        dateError:
            formError?.target === 'date'
                ? formError.message
                : availability?.kind === 'beforeEarliest'
                  ? t('operations.record.date.beforeEarliest')
                  : null,
        selected,
        onSelect,
        // A sole operation has nowhere to change to: the picker it would return
        // to holds one row, and the header says as much instead.
        onChangeOperation: sole ? null : onChangeOperation,
        shape: entryShape(selected, form, chart.data ?? [], locale),
        // The server's answer, never the client's arithmetic. `preview` is only
        // non-null once a request succeeded and stopped being stale, and the
        // totals are its own.
        isBalanced:
            preview !== null &&
            preview.totalDebitTetri === preview.totalCreditTetri &&
            preview.totalDebitTetri > 0,
        periodLabel: selected
            ? postingPeriodLabel(form.date, i18n.language)
            : null,
        postAmountLabel: preview ? formatGel(preview.totalDebitTetri) : null,
        form,
        onFieldChange,
        onSlotChange,
        accounts: chart.data ?? [],
        preview,
        isPreviewing: previewQuery.isFetching,
        isPreviewStale,
        amountError,
        formError,
        canPost,
        isPosting: record.isPending,
        onPost,
        recorded,
        onDismissRecorded,
    };
};
